import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import * as yup from 'yup';
import { Box, Grid } from '@mui/material';
import { formatPhoneNumber } from 'new/utils/numbers';
import Button from 'new/components/Button/Button';
import Modal from 'new/components/Modal';
import {
  emailSchema,
  phoneNumberSchema,
  stringRequiredSchema,
} from 'new/constants/schema';
import { REQUIRED_MESSAGE } from 'new/constants/validation';
import { PHONE_NUMBER_MASK } from 'new/constants/mask';
import { Form, FormikOnChangeHelper, InputSuberror } from 'new/components/Form';
import { AddUser, EditUserRequest } from 'new/modules/OfficeSettings/types';
import IconFontAwesome from 'new/components/IconFontAwesome';

type Props = {
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (values: AddUser) => void;
  editedUser?: EditUserRequest;
};

const StyledModal = styled(Modal)(({ theme }) => ({
  '.MuiPaper-root > .MuiTypography-root': {
    marginBottom: theme.spacing(3),
    textAlign: 'left',
    fontSize: '1.75rem',
  },

  button: {
    margin: `0 ${theme.spacing(1)}`,
  },
}));

const validationSchema = yup.object().shape({
  firstName: stringRequiredSchema,
  lastName: stringRequiredSchema,
  phone: phoneNumberSchema.required(REQUIRED_MESSAGE),
  email: emailSchema.required(REQUIRED_MESSAGE),
});

const EditUserModal: React.FC<Props> = ({
  editedUser,
  isLoading,
  onClose,
  onSubmit,
}) => {
  const initialValues = {
    firstName: editedUser?.firstName ?? '',
    lastName: editedUser?.lastName ?? '',
    phone: (editedUser?.phone && formatPhoneNumber(editedUser.phone)) ?? '',
    email: editedUser?.email ?? '',
  };
  const [isEmailChanged, setIsEmailChanged] = useState(false);

  const emailWarningIcon = isEmailChanged ? (
    <IconFontAwesome name="faTriangleExclamation" color="error" />
  ) : null;

  return (
    <StyledModal title="Edit User" open={!!editedUser} onClose={onClose}>
      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        <Grid container width={620} spacing={4}>
          <Grid item xs={4}>
            <Form.Input name="firstName" label="First Name" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <Form.Input name="lastName" label="Last Name" fullWidth />
          </Grid>
          <Grid item xs={4}>
            <Form.InputWithMask
              name="phone"
              label="Phone"
              mask={PHONE_NUMBER_MASK}
              guide
              fullWidth
              placeholder="XXX-XXX-XXXX"
            />
          </Grid>
          <Grid item xs={8}>
            <Form.Input
              name="email"
              label="Email"
              fullWidth
              InputProps={{
                endAdornment: emailWarningIcon,
              }}
            />
            {isEmailChanged && (
              <InputSuberror>
                FYI: Changing this email address will change the Member’s login
                to the Member Portal.
              </InputSuberror>
            )}
          </Grid>
          <FormikOnChangeHelper<{ email: string }>
            deps={({ values }) => [values.email]}
            onChange={({ values }) =>
              setIsEmailChanged(values.email !== editedUser!.email)
            }
          />
        </Grid>
        <Box width="100%" display="flex" justifyContent="center" mt={6}>
          <Button color="secondary" variant="outlined" onClick={onClose}>
            CANCEL
          </Button>
          <FormikOnChangeHelper onChange={({ dirty }) => [dirty]}>
            {({ dirty }) => (
              <Button type="submit" disabled={!dirty} isLoading={isLoading}>
                SAVE CHANGES
              </Button>
            )}
          </FormikOnChangeHelper>
        </Box>
      </Form>
    </StyledModal>
  );
};

export default EditUserModal;
